import * as React from 'react';

import { getLocalTimeZone, today, toZoned } from '@internationalized/date';
import {
  Weight,
  enumLoadStatus,
  enumLoadSortOption,
  LoadSortOption,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  DataGrid,
  Pagination,
  type PaginationProps,
  FloatingToolbar,
  ListScreenLayout,
  Badge,
  DateValue,
  useMemoDataGridRows,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucideFileCheck2,
  LucidePlusCircle,
  LucideTrash2,
  LucideX,
} from 'lucide-react';

import { $remoteFacilities } from '../../_state/facilities.state';
import { $checkLoadPhotoUploadsValidity } from '../../_state/loadDetail.state';
import * as state from '../../_state/loadsList.state';
import { createLoadsVoidValidationFetcher } from '../../_state/loadsList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { tradeRoutePaths } from '../../routes';
import { LoadRecord } from '../../types/load';
import { loadHasBeenShipped } from '../../utils/load';
import { enumEntityType, VoidEntityDialog } from '../_shared/VoidEntityDialog';

import { useLoadsListColumns } from './hooks/useLoadsListColumns';
import { LoadCreate } from './LoadCreate';
import { LoadListFilters } from './LoadListFilters';
import { LoadMarkAsReceivedDialog } from './LoadMarkAsReceivedDialog';
import { LoadMarkAsShippedDialog } from './LoadMarkAsShippedDialog';
import { LoadReconciliationTable } from './LoadReconciliationTable';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const COLUMN_TO_SORT_MAP: Record<
  string,
  {
    asc: LoadSortOption;
    desc: LoadSortOption;
  }
> = {
  expectedShipDate: {
    asc: enumLoadSortOption.ExpectedShipDateAsc,
    desc: enumLoadSortOption.ExpectedShipDateDesc,
  },
  shippedDate: {
    asc: enumLoadSortOption.ShippedDateAsc,
    desc: enumLoadSortOption.ShippedDateDesc,
  },
  status: {
    asc: enumLoadSortOption.StatusAsc,
    desc: enumLoadSortOption.StatusDesc,
  },
  createdOn: {
    asc: enumLoadSortOption.CreatedAtAsc,
    desc: enumLoadSortOption.CreatedAtDesc,
  },
  createdBy: {
    asc: enumLoadSortOption.CreatedByAsc,
    desc: enumLoadSortOption.CreatedByDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ LOADS LIST VIEW ⚛️ ⚛️ ⚛️
//
export const LoadsListView = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();

  // Refs
  const clearAllFiltersRef = React.useRef<(() => void) | null>(null);

  // States
  const [isShippedDialogOpen, setIsShippedDialogOpen] = React.useState(false);
  const [isReceivedDialogOpen, setIsReceivedDialogOpen] = React.useState(false);

  const [selectedKeys, setSelectedKeys] = useAtom(state.$selectedKeys);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [isDialogOpen, setIsDialogOpen] = useAtom(state.$isDialogOpen);
  const [selectedFacility] = useAtom(state.$selectedFacility);
  const [isReconciliationMode, setIsReconciliationMode] = useAtom(
    state.$isReconciliationMode,
  );
  const [selectedLoad, setSelectedLoad] = useAtom(state.$selectedLoad);
  const [isVoidDialogOpen, setIsVoidDialogOpen] = useAtom(
    state.$isVoidDialogOpen,
  );
  const [bulkVoidLoads, setBulkVoidLoads] = useAtom(state.$bulkVoidLoads);
  const [selectedLoadIdForShipped, setSelectedLoadIdForShipped] = useAtom(
    state.$selectedLoadIdForShipped,
  );
  const [selectedLoadIdForReceived, setSelectedLoadIdForReceived] = useAtom(
    state.$selectedLoadIdForReceived,
  );

  // Atoms
  const receivedLineItems = useAtomValue(state.$receivedLineItems);
  const selectedStatuses = useAtomValue(state.$statuses);
  const searchTerm = useAtomValue(state.$searchTerm);
  const hasFiltersActive = useAtomValue(state.$filtersActive);
  const columnSelectedStatuses = useAtomValue(state.$columnSelectedStatuses);
  const setCurrentSorting = useSetAtom(state.$currentSorting);
  const {
    data,
    isFetching,
    loadingState,
    error,
    refetch: refetchLoads,
  } = useAtomValue(state.$remoteLoads);
  const { refetch: refetchStatusCounts } = useAtomValue(
    state.$loadsCountingByStatus,
  );
  const voidLoad = useSetAtom(state.$voidLoad);
  const markAsShipped = useSetAtom(state.$markAsShipped);
  const markAsReceived = useSetAtom(state.$markAsReceived);

  // Memoized values
  const fetchLoadsVoidValidation = React.useMemo(
    () => createLoadsVoidValidationFetcher(),
    [],
  );

  const toast = useToast();
  const loads = data?.loads ?? [];
  const total = data?.total ?? 0;
  const selectedRows = useMemoDataGridRows(loads, selectedKeys);

  // Callbacks
  const handleRefetchLoads = React.useCallback(() => {
    refetchLoads();
    refetchStatusCounts();
  }, [refetchLoads, refetchStatusCounts]);

  const handlePaginationChange = React.useCallback<
    NonNullable<PaginationProps['onChange']>
  >(
    ({ limit: newLimit, offset: newOffset }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );

  const handleCreate = React.useCallback(() => {
    setIsDialogOpen(true);
  }, [setIsDialogOpen]);

  const handleBulkVoid = React.useCallback(() => {
    const loadsToVoid = selectedRows
      .filter((row) => row.id && row.loadNumber !== undefined)
      .map((row) => ({
        id: row.id as string,
        displayNumber: String(row.loadNumber),
        status: row.status,
      }));

    if (loadsToVoid.length > 0) {
      setBulkVoidLoads(loadsToVoid);
      setIsVoidDialogOpen(true);
    }
  }, [selectedRows, setBulkVoidLoads, setIsVoidDialogOpen]);

  const handleBulkVoidComplete = React.useCallback(() => {
    const loadNumbers = bulkVoidLoads
      .map((l) => `#${l.displayNumber}`)
      .join(', ');
    toast.success(
      t.loadsView.messages.bulkVoidSuccess(bulkVoidLoads.length, loadNumbers),
    );
    setSelectedKeys(new Set());
    setIsVoidDialogOpen(false);
    setBulkVoidLoads([]);
    handleRefetchLoads();
  }, [
    toast,
    bulkVoidLoads,
    setSelectedKeys,
    setIsVoidDialogOpen,
    setBulkVoidLoads,
    handleRefetchLoads,
  ]);

  const handleBulkVoidCancel = React.useCallback(() => {
    setIsVoidDialogOpen(false);
    setBulkVoidLoads([]);
  }, [setIsVoidDialogOpen, setBulkVoidLoads]);

  const bookingIdByLoadId = useAtomValue(state.$bookingIdByLoadId);

  const handleLoadClick = React.useCallback(
    (load: LoadRecord) => {
      navigate(tradeRoutePaths.loadDetail, {
        params: { id: load.id },
        query: loadHasBeenShipped(load.status) ? { tab: 'materials' } : {},
        keepSearchParams: false,
      });
    },
    [navigate],
  );

  const handleBackToLoads = React.useCallback(() => {
    setIsReconciliationMode(false);
    setSelectedLoad(null);
  }, [setIsReconciliationMode, setSelectedLoad]);

  const handleVoidLoad = React.useCallback(
    (loadId: string) => {
      const load = loads.find((l) => l.id === loadId);
      if (!load) return;

      setBulkVoidLoads([
        {
          id: load.id as string,
          displayNumber: String(load.loadNumber),
          status: load.status,
        },
      ]);
      setIsVoidDialogOpen(true);
    },
    [loads, setBulkVoidLoads, setIsVoidDialogOpen],
  );

  const handleVoidConfirm = React.useCallback(
    async (id: string, _forceUnlink: boolean) => {
      try {
        await voidLoad({ loadId: id });
      } catch (error) {
        toast.error(
          `Failed to void load: ${error instanceof Error ? error.message : 'Unknown error'}`,
        );
        throw error;
      }
    },
    [voidLoad, toast],
  );

  const handleShipLoad = React.useCallback(
    async (
      shippedDate: DateValue | undefined,
      lightWeight: Weight | null,
      heavyWeight: Weight | null,
    ) => {
      await markAsShipped({
        loadId: selectedLoadIdForShipped ?? '',
        shippedDate: toZoned(
          shippedDate ?? today(getLocalTimeZone()),
          getLocalTimeZone(),
        ).toAbsoluteString(),
        lightWeight: lightWeight,
        heavyWeight: heavyWeight,
      });
      setIsShippedDialogOpen(false);
      setSelectedLoadIdForShipped(null);
    },
    [markAsShipped, selectedLoadIdForShipped, setSelectedLoadIdForShipped],
  );

  const handleReceiveLoad = React.useCallback(
    async (receivedDate: DateValue | undefined) => {
      await markAsReceived({
        loadId: selectedLoadIdForReceived ?? '',
        receivedDate: receivedDate ?? today(getLocalTimeZone()),
        receivedLineItems,
      });

      setIsReceivedDialogOpen(false);
      setSelectedLoadIdForReceived(null);
    },
    [
      markAsReceived,
      selectedLoadIdForReceived,
      setIsReceivedDialogOpen,
      setSelectedLoadIdForReceived,
      receivedLineItems,
    ],
  );

  const handleReconcileLoad = React.useCallback(() => {
    // Switch to reconciliation mode, same as clicking on a received load
    setIsReconciliationMode(true);
  }, [setIsReconciliationMode]);

  const checkLoadPhotoUploadsValidity = useAtomValue(
    $checkLoadPhotoUploadsValidity,
  );

  const handleMarkAsShipped = React.useCallback(
    async (loadId: string) => {
      const bookingId = bookingIdByLoadId(loadId);

      if (bookingId) {
        const isValidForShip = await checkLoadPhotoUploadsValidity(
          loadId,
          bookingId,
        );
        if (!isValidForShip) {
          navigate(tradeRoutePaths.loadDetail, {
            params: { id: loadId },
            query: { tab: 'photos' },
            keepSearchParams: false,
          });
          return;
        }
      }

      setSelectedLoadIdForShipped(loadId);
      setIsShippedDialogOpen(true);
    },
    [
      setSelectedLoadIdForShipped,
      setIsShippedDialogOpen,
      bookingIdByLoadId,
      checkLoadPhotoUploadsValidity,
      navigate,
    ],
  );

  const handleMarkAsReceived = React.useCallback(
    (loadId: string) => {
      setSelectedLoadIdForReceived(loadId);
      setIsReceivedDialogOpen(true);
    },
    [setSelectedLoadIdForReceived, setIsReceivedDialogOpen],
  );

  const handleClearAllFilters = React.useCallback(() => {
    clearAllFiltersRef.current?.();
  }, []);
  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumLoadSortOption.CreatedAtDesc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }

      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );

  // Other data hooks
  const { data: facilitiesData, refetch: refetchFacilities } =
    useAtomValue($remoteFacilities);
  const facilities = facilitiesData?.facilities ?? [];
  const { columns, columnVisibilityState } = useLoadsListColumns({
    status: selectedStatuses[0],
    facilities,
    onDoubleClick: handleLoadClick,
    onVoid: handleVoidLoad,
    onReconcile: handleReconcileLoad,
    onMarkAsShipped: handleMarkAsShipped,
    onMarkAsReceived: handleMarkAsReceived,
    onRefetch: handleRefetchLoads,
  });

  // Effects
  /** Ensure facility data is loaded when not yet available. */
  React.useEffect(() => {
    if (!facilitiesData) {
      refetchFacilities();
    }
  }, [facilitiesData, refetchFacilities]);

  // Custom empty panel props for loads
  const emptyPanelProps = {
    title: t.loadsView.emptyState.title,
    description: (
      <span>
        {t.loadsView.emptyState.subtitle(
          <strong>{t.loadsView.emptyState.action}</strong>,
        )}
      </span>
    ),
    action: (
      <Button
        onPress={handleCreate}
        variant="solid"
        color="primary"
        icon={<LucidePlusCircle />}
      >
        {t.loadsView.toolbar.create}
      </Button>
    ),
    centered: true,
  };

  const emptyResultsPanelProps = {
    title: t.loadsView.emptyResultsState.title,
    description: t.loadsView.emptyResultsState.subtitle,
    action: (
      <Button
        onPress={handleClearAllFilters}
        variant="solid"
        color="primary"
        icon={<LucidePlusCircle />}
      >
        {t.loadsView.emptyResultsState.action(
          searchTerm?.trim()?.length > 0
            ? t.loadsView.filters.search.placeholder
            : undefined,
        )}
      </Button>
    ),
    centered: true,
  };

  const reconciliationLoadsLength = loads.filter(
    (load) => load.status === enumLoadStatus.Received,
  ).length;
  const filteringReconciliation = columnSelectedStatuses.has(
    enumLoadStatus.Received,
  );

  if (isReconciliationMode && !error) {
    return (
      <div className="@LoadsListView flex h-full flex-col">
        <LoadReconciliationTable
          onBack={handleBackToLoads}
          selectedFacility={selectedFacility}
          initialSelectedLoad={selectedLoad}
        />
      </div>
    );
  }

  return (
    <div className="@LoadsListView flex h-full flex-col">
      <ListScreenLayout
        filters={<LoadListFilters clearAllFiltersRef={clearAllFiltersRef} />}
        actions={
          <>
            {reconciliationLoadsLength > 0 && filteringReconciliation && (
              <Button
                variant="outline"
                color="neutral"
                icon={<LucideFileCheck2 />}
                onPress={handleReconcileLoad}
              >
                {t.loadsView.filters.bulkReconciliation}{' '}
                <span className="text-neutral/60">
                  ({reconciliationLoadsLength})
                </span>
              </Button>
            )}
            <Button
              onPress={handleCreate}
              variant="solid"
              color="primary"
              icon={<LucidePlusCircle />}
            >
              {t.loadsView.toolbar.create}
            </Button>
          </>
        }
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        <DataGrid<LoadRecord>
          isLoading={loadingState.isInitialLoading || isFetching}
          columns={columns}
          dataSource={loads}
          rowKey="id"
          aria-label={t.loadsView.table.aria}
          selectionMode="multiple"
          onSelectionChange={setSelectedKeys}
          onColumnSort={handleColumnSort}
          columnVisibilityState={columnVisibilityState}
          className="w-full text-sm"
          fullWidth
          persistColumnOrderKey="trade:loads-list"
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.loadsView.content.error(error.message),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={
            hasFiltersActive ? emptyResultsPanelProps : emptyPanelProps
          }
        />
      </ListScreenLayout>

      <FloatingToolbar visible={selectedRows.length > 0}>
        <div className="flex flex-row items-center justify-between gap-3">
          <div className="flex flex-row items-center gap-2 text-sm">
            <Badge color="primary" size="small">
              {selectedRows.length}
            </Badge>{' '}
            {t.loadsView.toolbar.selected}
          </div>
          <Button
            variant="outline"
            icon={<LucideTrash2 />}
            isDisabled={selectedRows.length < 1}
            onPress={handleBulkVoid}
          >
            {t.loadsView.toolbar.delete(selectedRows.length)}
          </Button>
          <LucideX
            className="size-4 cursor-pointer text-neutral/80"
            onClick={() => setSelectedKeys(new Set())}
          />
        </div>
      </FloatingToolbar>

      {isDialogOpen && (
        <LoadCreate
          isOpen={isDialogOpen}
          onClose={() => {
            setIsDialogOpen(false);
          }}
        />
      )}

      {/* Void Load Dialog */}
      {isVoidDialogOpen && bulkVoidLoads.length > 0 && (
        <VoidEntityDialog
          entityType={enumEntityType.load}
          entities={bulkVoidLoads}
          onVoid={handleVoidConfirm}
          fetchValidation={fetchLoadsVoidValidation}
          onComplete={handleBulkVoidComplete}
          onCancel={handleBulkVoidCancel}
        />
      )}

      {/* Status transition dialogs */}
      {isShippedDialogOpen && (
        <LoadMarkAsShippedDialog
          onMarkAsShipped={handleShipLoad}
          onCancel={() => setIsShippedDialogOpen(false)}
        />
      )}

      {isReceivedDialogOpen && (
        <LoadMarkAsReceivedDialog
          onMarkAsReceived={handleReceiveLoad}
          onCancel={() => setIsReceivedDialogOpen(false)}
        />
      )}
    </div>
  );
};
